// src/components/layout/TopBar.jsx - FIXED with proper usage stats fetching

import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from 'react-query';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
    BellIcon,
    UserCircleIcon,
    Cog6ToothIcon,
    ArrowRightOnRectangleIcon,
    Bars3Icon,
    XMarkIcon,
    ChartBarIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

// Usage Stats Hook for TopBar
const useUsageStats = () => {
    const { user } = useAuth();

    return useQuery(
        'topBarUsageStats',
        () => fetch('/api/settings/usage-stats', {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        }).then(res => {
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            return res.json();
        }),
        {
            retry: 1,
            staleTime: 60000, // 1 minute
            refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
            enabled: !!user, // Only fetch if user is logged in
            onError: (error) => {
                console.error('TopBar usage stats fetch error:', error);
            }
        }
    );
};

// Usage Stats Indicator Component
const UsageStatsIndicator = () => {
    const { user } = useAuth();
    const { data: usageResponse, isLoading, error } = useUsageStats();

    if (!user || isLoading) {
        return (
            <div className="hidden sm:flex items-center space-x-2 px-3 py-1 bg-gray-100 rounded-md">
                <div className="animate-pulse">
                    <div className="h-4 bg-gray-300 rounded w-12"></div>
                </div>
            </div>
        );
    }

    if (error) {
        console.error('Usage stats error:', error);
        return (
            <div className="hidden sm:flex items-center space-x-2 px-3 py-1 bg-red-50 border border-red-200 rounded-md">
                <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
                <span className="text-sm text-red-700">Usage unavailable</span>
            </div>
        );
    }

    const usageStats = usageResponse?.data?.usage || {
        generated: 0,
        remaining: 10,
        limit: 10,
        usagePercent: 0
    };

    const plan = user?.plan || 'free';
    const generated = usageStats.generated || 0;
    const limit = usageStats.limit || 10;
    const remaining = usageStats.remaining || (limit - generated);
    const usagePercent = usageStats.usagePercent || Math.round((generated / limit) * 100);

    // Determine indicator color based on usage
    const getIndicatorColor = () => {
        if (usagePercent >= 95) return 'bg-red-500';
        if (usagePercent >= 80) return 'bg-yellow-500';
        if (usagePercent >= 50) return 'bg-blue-500';
        return 'bg-green-500';
    };

    const getTextColor = () => {
        if (usagePercent >= 95) return 'text-red-700';
        if (usagePercent >= 80) return 'text-yellow-700';
        return 'text-gray-700';
    };

    const getBgColor = () => {
        if (usagePercent >= 95) return 'bg-red-50 border-red-200';
        if (usagePercent >= 80) return 'bg-yellow-50 border-yellow-200';
        return 'bg-gray-50 border-gray-200';
    };

    return (
        <div className="hidden sm:flex items-center">
            {/* Usage Stats Dropdown */}
            <Menu as="div" className="relative">
                <Menu.Button
                    className={`flex items-center space-x-2 px-3 py-1 rounded-md border transition-colors ${getBgColor()} hover:shadow-md`}
                    title="View usage statistics"
                >
                    <ChartBarIcon className="h-4 w-4 text-gray-500" />
                    <span className={`text-sm font-medium ${getTextColor()}`}>
                        {generated}/{limit}
                    </span>
                    {usagePercent >= 80 && (
                        <ExclamationTriangleIcon className={`h-4 w-4 ${usagePercent >= 95 ? 'text-red-500' : 'text-yellow-500'}`} />
                    )}
                    <div className={`w-2 h-2 rounded-full ${getIndicatorColor()}`}></div>
                </Menu.Button>

                <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <Menu.Items className="absolute right-0 mt-2 w-72 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                        <div className="p-4">
                            <h3 className="text-sm font-medium text-gray-900 mb-3">Monthly Usage Stats</h3>

                            {/* Usage Progress Bar */}
                            <div className="mb-3">
                                <div className="flex justify-between text-xs text-gray-600 mb-1">
                                    <span>Posts Generated</span>
                                    <span className={getTextColor()}>
                                        {generated}/{limit} ({usagePercent}%)
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className={`h-2 rounded-full transition-all duration-300 ${getIndicatorColor()}`}
                                        style={{ width: `${Math.min(usagePercent, 100)}%` }}
                                    ></div>
                                </div>
                            </div>

                            {/* Stats Grid */}
                            <div className="grid grid-cols-3 gap-2 text-center text-xs mb-3">
                                <div className="bg-blue-50 rounded p-2">
                                    <div className="font-bold text-blue-600">{generated}</div>
                                    <div className="text-blue-700">Generated</div>
                                </div>
                                <div className="bg-green-50 rounded p-2">
                                    <div className="font-bold text-green-600">{remaining}</div>
                                    <div className="text-green-700">Remaining</div>
                                </div>
                                <div className="bg-purple-50 rounded p-2">
                                    <div className="font-bold text-purple-600">{usageStats.published || 0}</div>
                                    <div className="text-purple-700">Published</div>
                                </div>
                            </div>

                            {/* Plan Info */}
                            <div className="border-t pt-3">
                                <div className="flex justify-between items-center text-xs">
                                    <span className="text-gray-600">Current Plan:</span>
                                    <span className="font-medium text-gray-900 capitalize">{plan}</span>
                                </div>
                                {usageStats.lastResetDate && (
                                    <div className="text-xs text-gray-500 mt-1">
                                        Resets monthly • Last: {new Date(usageStats.lastResetDate).toLocaleDateString()}
                                    </div>
                                )}
                            </div>

                            {/* Warning/Upgrade CTA */}
                            {usagePercent >= 80 && (
                                <div className={`mt-3 p-2 rounded text-xs ${usagePercent >= 95 ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'}`}>
                                    <div className="flex items-start">
                                        <ExclamationTriangleIcon className={`h-4 w-4 ${usagePercent >= 95 ? 'text-red-500' : 'text-yellow-500'} mr-1 flex-shrink-0`} />
                                        <div>
                                            <div className="font-medium">
                                                {usagePercent >= 95 ? 'Quota Almost Exceeded' : 'High Usage Warning'}
                                            </div>
                                            <div className="mt-1">
                                                You've used {usagePercent}% of your monthly quota.
                                                {plan === 'free' && ' Consider upgrading for more posts.'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="mt-3 pt-3 border-t space-y-2">
                                <Menu.Item>
                                    {({ active }) => (
                                        <Link
                                            to="/billing"
                                            className={`${active ? 'bg-gray-100' : ''} flex items-center w-full px-2 py-1 text-xs text-gray-700 rounded`}
                                        >
                                            <ChartBarIcon className="h-4 w-4 mr-2" />
                                            View Billing & Usage
                                        </Link>
                                    )}
                                </Menu.Item>
                                {plan === 'free' && (
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                to="/billing"
                                                className={`${active ? 'bg-blue-50' : ''} flex items-center w-full px-2 py-1 text-xs text-blue-700 rounded font-medium`}
                                            >
                                                🚀 Upgrade Plan
                                            </Link>
                                        )}
                                    </Menu.Item>
                                )}
                            </div>
                        </div>
                    </Menu.Items>
                </Transition>
            </Menu>
        </div>
    );
};

function TopBar({ onToggleMobileMenu, mobileMenuOpen }) {
    const { user, logout } = useAuth();
    const { data: usageResponse } = useUsageStats();

    // Extract usage stats for user menu display
    const usageStats = usageResponse?.data?.usage || {};
    const postsGenerated = usageStats.generated || 0;
    const limit = usageStats.limit || 10;

    return (
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
                {/* Mobile menu button */}
                <div className="flex items-center lg:hidden">
                    <button
                        className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onClick={onToggleMobileMenu}
                        aria-label="Toggle mobile menu"
                    >
                        {mobileMenuOpen ? (
                            <XMarkIcon className="h-6 w-6" />
                        ) : (
                            <Bars3Icon className="h-6 w-6" />
                        )}
                    </button>
                </div>

                {/* Left side - Mobile logo */}
                <div className="flex-1 lg:hidden flex justify-center">
                    <div className="flex items-center">
                        <span className="text-xl font-bold text-blue-600">PostMgr</span>
                    </div>
                </div>

                {/* Desktop left side - can add breadcrumbs or page title here */}
                <div className="flex-1 hidden lg:block">
                    {/* Space for future breadcrumbs or page title */}
                </div>

                {/* Right side - user controls */}
                <div className="flex items-center space-x-4">
                    {/* ADDED: Usage Stats Indicator */}
                    <UsageStatsIndicator />

                    {/* Notifications */}
                    <button className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-md">
                        <BellIcon className="h-6 w-6" />
                    </button>

                    {/* User menu */}
                    <Menu as="div" className="relative">
                        <Menu.Button className="flex items-center p-2 text-sm rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <UserCircleIcon className="h-8 w-8 text-gray-400 mr-2" />
                            <div className="text-left hidden sm:block">
                                <div className="text-sm font-medium text-gray-900">
                                    {user?.firstName} {user?.lastName}
                                </div>
                                <div className="text-xs text-gray-500 capitalize">
                                    {user?.plan || 'free'} plan
                                </div>
                            </div>
                        </Menu.Button>

                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                                <div className="py-1">
                                    {/* User info header - FIXED with proper usage data */}
                                    <div className="px-4 py-3 border-b border-gray-100">
                                        <p className="text-sm font-medium text-gray-900">
                                            {user?.firstName} {user?.lastName}
                                        </p>
                                        <p className="text-sm text-gray-500">{user?.email}</p>
                                        <p className="text-xs text-gray-400 mt-1 capitalize">
                                            {user?.plan || 'free'} plan • {postsGenerated}/{limit} posts this month
                                        </p>
                                        {/* ADDED: Usage progress in user menu */}
                                        {limit > 0 && (
                                            <div className="mt-2">
                                                <div className="w-full bg-gray-200 rounded-full h-1">
                                                    <div
                                                        className={`h-1 rounded-full transition-all duration-300 ${
                                                            (postsGenerated / limit) >= 0.95 ? 'bg-red-500' :
                                                                (postsGenerated / limit) >= 0.8 ? 'bg-yellow-500' :
                                                                    'bg-green-500'
                                                        }`}
                                                        style={{ width: `${Math.min((postsGenerated / limit) * 100, 100)}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Menu items */}
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                to="/settings"
                                                className={`${active ? 'bg-gray-100' : ''} flex items-center px-4 py-2 text-sm text-gray-700`}
                                            >
                                                <Cog6ToothIcon className="h-4 w-4 mr-3" />
                                                Settings
                                            </Link>
                                        )}
                                    </Menu.Item>

                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                to="/billing"
                                                className={`${active ? 'bg-gray-100' : ''} flex items-center px-4 py-2 text-sm text-gray-700`}
                                            >
                                                <ChartBarIcon className="h-4 w-4 mr-3 text-green-600" />
                                                Billing & Usage
                                            </Link>
                                        )}
                                    </Menu.Item>

                                    <div className="border-t border-gray-100"></div>

                                    <Menu.Item>
                                        {({ active }) => (
                                            <button
                                                onClick={logout}
                                                className={`${active ? 'bg-gray-100' : ''} flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                                            >
                                                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                                                Sign out
                                            </button>
                                        )}
                                    </Menu.Item>
                                </div>
                            </Menu.Items>
                        </Transition>
                    </Menu>
                </div>
            </div>
        </div>
    );
}

export default TopBar;